import { computed, ref } from '@vue/composition-api';
import { ApiUrls, RoomErrors, UserType } from '@/enums';
import useSocket from '@/compositions/useSocket';
import { v4 as uuidv4 } from 'uuid';
import * as jwt from 'jsonwebtoken';
import useTracks from '@/compositions/useTracks';
import useStreams from '@/compositions/useStreams';
import api from '../../api';

const room = ref('');
const auth = ref({});
const candidate = ref({});
const recordingRoom = ref(false);
const existingRoom = ref(null);
const user = ref(null);
const error = ref(null);
const closed = ref(false);
const leftRoom = ref(false);
const setupDone = ref(false);
const invitations = ref(null);

export default function useRoom() {
  const { sendEvent, registerMessage } = useSocket();
  const { stopAllStreamTracks } = useStreams();

  async function fetchInvitations() {
    if (!auth.value.secret) {
      return;
    }
    const response = await api.get(`${ApiUrls.V1}/rooms/roomId/${room.value}/invitations`, {
      params: {
        secret: auth.value.secret,
      },
    });
    invitations.value = response.data;
  }

  async function login() {
    try {
      const token = await api.post(`${ApiUrls.V1}/auth/login`, {
        room: room.value,
        secret: auth.value?.secret || candidate.value?.secret,
      });

      await fetchInvitations();
      const data = jwt.decode(token.data?.accessToken);
      localStorage.setItem('accessToken', JSON.stringify(data));
      user.value = data;
    } catch (e) {
      console.error(e);
      error.value = RoomErrors.UNAUTHORIZED;
    }
  }

  function setRoom(roomVal) {
    room.value = roomVal;
  }

  function setCandidateAuth(authData) {
    candidate.value = authData;
  }

  function setAuth(authData) {
    auth.value = authData;
  }

  function generateRoom() {
    auth.value = { secret: uuidv4() };
    window.location.href = `/?room=${uuidv4()}&secret=${auth.value.secret}`;
  }

  async function createRoom() {
    candidate.value = { secret: uuidv4() };
    const newRoom = await api.post(`${ApiUrls.V1}/rooms`, {
      roomId: room.value.toString(),
      auth: { admin: auth.value, candidate: candidate.value },
    });
    existingRoom.value = newRoom.data;
  }

  function registerEvents() {
    registerMessage('room:recording', (data) => {
      recordingRoom.value = data;
    });
    registerMessage('room:closed', () => {
      stopAllStreamTracks();
      closed.value = true;
    });
  }

  function closeRoom() {
    sendEvent('action:close-room', { roomId: room.value, auth: auth.value });
    stopAllStreamTracks();
    closed.value = true;
  }

  async function processRecordRoom(state) {
    try {
      const body = {
        state,
        roomId: room.value.toString(),
        auth: auth.value,
      };
      await sendEvent('action:record', body);
    } catch (e) {
      console.error('VideoRoomPublisherJanusPlugin, cannot set record', e);
    }
  }

  async function updateRoom(data) {
    await api.put(`${ApiUrls.V1}/rooms/${existingRoom.value?._id}`, data);
  }

  async function fetchRoom() {
    const query = {
      where: {
        roomId: room.value.toString(),
      },
    };
    const existRoom = await api.get(`${ApiUrls.V1}/rooms`, {
      params: {
        query: JSON.stringify(query),
      },
    });

    if (!existRoom.data.data.length) {
      return;
    }

    existingRoom.value = existRoom.data.data[0];
    recordingRoom.value = existingRoom.value.recording;
    closed.value = existingRoom.value.closed;
  }

  async function findOrCreateRoom() {
    await fetchRoom();

    if (!existingRoom.value) {
      await createRoom();
    }
    registerEvents();
  }

  return {
    setRoom,
    generateRoom,
    findOrCreateRoom,
    fetchRoom,
    updateRoom,
    existingRoom,
    setAuth,
    setCandidateAuth,
    candidate,
    auth,
    processRecordRoom,
    room: computed(() => room.value),
    recordingRoom: computed(() => recordingRoom.value),
    login,
    user: {
      ...user,
      isAdmin: computed(() => user.value?.role === UserType.ADMIN),
      isCandidate: computed(() => user.value?.role === UserType.CANDIDATE),
    },
    error,
    closeRoom,
    closed,
    leftRoom,
    setupDone,
    invitations,
  };
}
